import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useKYPSearchState } from './hooks/useKYPSearchState';
import { useKYPSearchCache } from './hooks/useKYPSearchCache';
import { usePlayerDataService } from './services/playerDataService';
import { useSearchService } from './services/searchService';
import { useAsyncSearchService } from './services/asyncSearchService';
import { CategoryPollingManager } from './services/CategoryPollingManager';
import { KYPSearchContextType } from './types/kyp-search.types';
import { useAuth } from '../auth.context';

// Create context with undefined initial value
const KYPSearchContext = createContext<KYPSearchContextType | undefined>(undefined);

export const KYPSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, actions] = useKYPSearchState();
  const cache = useKYPSearchCache(state, actions);
  const playerDataService = usePlayerDataService();
  const searchService = useSearchService();
  const { getToken } = useAuth();
  const categoryPollingManagerRef = useRef<CategoryPollingManager | null>(null);
  
  // Create abort controller reference for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Create a new abort controller when needed
  if (!abortControllerRef.current) {
    abortControllerRef.current = new AbortController();
  }
  
  // Initialize the async search service with callbacks
  const asyncSearchService = useAsyncSearchService({
    onStatusUpdate: (job) => actions.setSearchJob(job),
    onResultsReady: (results) => {
      actions.setPlayerData(results);
      actions.setResultsProcessed(true);
      actions.setError(null);
    },
    onError: (error) => actions.setError(error),
    onLoading: (loading) => actions.setIsLoading(loading)
  });

  const handleNameSearch = useCallback(async () => {
    if (!state.playerName.trim()) {
      actions.setError('Please enter a player name');
      return;
    }

    try {
      // Cancel any previous requests
      if (abortControllerRef.current) {
        console.log('Cancelling previous name search request');
        abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
      }
      
      actions.setIsSearchingName(true);
      actions.setHasSearched(true);
      actions.setPlayerMatches([]);
      actions.setDetailedPlayerMatches([]);
      actions.setPlayerData(null);
      actions.setError(null);

      // Check cache first
      const cachedPlayer = cache.findPlayerByName(state.playerName);

      if (cachedPlayer) {
        // Load directly from cache
        cache.loadCachedPlayer(cachedPlayer);
        actions.setIsSearchingName(false);
        return;
      }
      
      // Search for players by name
      const { basicMatches, detailedMatches } = await searchService.searchPlayersByName(state.playerName);
      
      actions.setPlayerMatches(basicMatches);
      actions.setDetailedPlayerMatches(detailedMatches);

      // If only one match, automatically select it
      if (basicMatches.length === 1 || detailedMatches.length === 1) {
        const matchId = detailedMatches.length === 1 ? detailedMatches[0].id : basicMatches[0].id;
        const matchName = detailedMatches.length === 1 ? detailedMatches[0].name : basicMatches[0].name;
        
        actions.setPlayerId(matchId);
        await handlePlayerSearch(matchId, matchName);
      } else if (basicMatches.length > 1 || detailedMatches.length > 1) {
        actions.setPlayerData(null);
      }
    } catch (error) {
      // Don't set error if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Name search request was aborted');
        return;
      }
      
      actions.setError('Error: Failed to fetch player matches');
      console.error('Name search error:', error);
    } finally {
      actions.setIsSearchingName(false);
    }
  }, [state.playerName, actions, cache, searchService]);

  const handlePlayerSearch = useCallback(async (id: string, name?: string) => {
    if (!id.trim()) {
      actions.setError('Please enter a player ID');
      return;
    }
    
    try {
      // Cancel any previous requests
      if (abortControllerRef.current) {
        console.log('Cancelling previous player search request');
        abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
      }
      
      // Reset search state
      actions.resetSearchState();
      
      // Clear existing player data to ensure UI resets properly
      actions.setPlayerData(null);
      
      // Update UI state
      actions.setIsLoading(true);
      actions.setError(null);
      if (name) {
        actions.setPlayerName(name);
      }
      actions.setPlayerId(id);
      actions.setHasSearched(true);
      
      // Invalidate the playerTransferStats cache to ensure fresh data is fetched
      try {
        // Import the queryClient dynamically to avoid circular dependencies
        const { queryClient } = await import('../queryClient');
        console.log('Invalidating playerTransferStats cache for new search');
        queryClient.invalidateQueries({ queryKey: ['playerTransferStats'] });
      } catch (error) {
        console.error('Error invalidating playerTransferStats cache:', error);
      }
      
      // Step 1: Fetch critical player data FIRST, before checking automated results or starting KYP search
      console.log(`Fetching critical player data for ID: ${id} BEFORE starting KYP search`);
      
      // Fetch player profile
      const profile = await playerDataService.fetchPlayerProfile(id);
      if (profile) {
        actions.setPlayerProfile(profile);
      }
      
      // Fetch player statistics and calculate career stats
      const stats = await playerDataService.fetchPlayerStats(id);
      if (stats) {
        actions.setPlayerStats(stats);
        
        // Calculate and set career stats
        const careerStats = playerDataService.calculateCareerStats(stats);
        if (careerStats) {
          actions.setPlayerCareerStats(careerStats);
        }
      }
      
      // Now check for automated results
      console.log('Checking for automated results for player ID:', id);
      const { hasResults, data: automatedData, timestamp } = await searchService.checkAutomatedResults(id);
      
      if (hasResults && automatedData) {
        console.log('Using existing automated results for player ID:', id);
        actions.setPlayerData(automatedData);
        actions.setIsAutomatedResult(true);
        actions.setAutomatedResultTimestamp(timestamp || null);
        
        // Fetch supplementary data
        const supplementaryData = await playerDataService.fetchSupplementaryData(id);
        
        if (supplementaryData.injuries) actions.setPlayerInjuries(supplementaryData.injuries);
        if (supplementaryData.transfers) actions.setPlayerTransfers(supplementaryData.transfers);
        if (supplementaryData.achievements) actions.setPlayerAchievements(supplementaryData.achievements);
        
        // Prefetch tab data
        playerDataService.prefetchTabData(id).catch(console.error);
        
        // Cache the player data
        if (profile && automatedData) {
          const cachedPlayer = {
            id,
            name: name || state.playerName,
            profile,
            data: automatedData,
            injuries: supplementaryData.injuries || undefined,
            transfers: supplementaryData.transfers || undefined,
            achievements: supplementaryData.achievements || undefined,
            stats: stats || undefined
          };
          
          cache.addPlayer(cachedPlayer);
        }
        
        actions.setIsLoading(false);
        return;
      }
      
      // If no automated results, start a new search
      console.log('No automated results found, starting new search for player ID:', id);
      await startAsyncSearch(id, name);
    } catch (error) {
      // Don't set error if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Player search request was aborted');
        return;
      }
      
      actions.setError('Error: Failed to fetch player data');
      console.error('Search error:', error);
      actions.setIsLoading(false);
    }
  }, [
    actions, 
    playerDataService, 
    searchService, 
    cache, 
    state.playerName
  ]);

  const startAsyncSearch = useCallback(async (id: string, name?: string) => {
    try {
      // Clear player data first
      actions.clearPlayerData();
      
      // Only keep the player profile if we already have it
      if (state.playerProfile && state.playerProfile.id !== id) {
        actions.setPlayerProfile(null);
      }
      
      // Update name and ID
      if (name) {
        actions.setPlayerName(name);
      }
      actions.setPlayerId(id);

      if (state.kypPipelineType === 'parallel_categories') {
        const token = getToken();
        if (!token) {
          actions.setError("Authentication token not found.");
          return;
        }
        const { kypV3ApiService } = await import('../services/kypV3ApiService');
        const result = await kypV3ApiService.executeParallelCategorySearch(
          id,
          name || state.playerName,
          token,
          state.stackedKYPConfig.forceResummary
        );
        if (result.status === 'started' && result.search_id) {
          initializeCategoryPolling(result.search_id, 'parallel_categories');
          actions.setSearchJob({ id: parseInt(result.search_id, 10), status: 'in_progress' });
          actions.setIsLoading(false);
        } else {
          throw new Error('Failed to start parallel category search');
        }
      } else if (state.kypPipelineType === 'parallel_categories_mock') {
        console.log('[KYP Context] Starting mock parallel category search');
        const token = getToken();
        if (!token) {
          actions.setError("Authentication token not found.");
          return;
        }
        const { kypV3ApiService } = await import('../services/kypV3ApiService');
        console.log('[KYP Context] Calling executeMockParallelCategorySearch with playerId:', id);
        const result = await kypV3ApiService.executeMockParallelCategorySearch(id, token);
        console.log('[KYP Context] Mock search result:', result);
        if (result.status === 'started' && result.search_id) {
          console.log('[KYP Context] Initializing category polling for mock search with ID:', result.search_id);
          initializeCategoryPolling(result.search_id, 'parallel_categories_mock');
          actions.setSearchJob({ id: parseInt(result.search_id, 10), status: 'in_progress' });
          actions.setIsLoading(false);
        } else {
          throw new Error('Failed to start mock parallel category search');
        }
      } else {
        // Start the async search
        await asyncSearchService.startAsyncSearch(id, name || state.playerName);
      }
      
      // Fetch supplementary data while the search is running
      playerDataService.fetchSupplementaryData(id).then(data => {
        if (data.injuries) actions.setPlayerInjuries(data.injuries);
        if (data.transfers) actions.setPlayerTransfers(data.transfers);
        if (data.achievements) actions.setPlayerAchievements(data.achievements);
      }).catch(console.error);
      
      // Prefetch tab data in the background
      playerDataService.prefetchTabData(id).catch(console.error);
    } catch (error) {
      console.error('Error starting async search:', error);
      actions.setError(`Error: ${error instanceof Error ? error.message : 'Failed to start search'}`);
      actions.setIsLoading(false);
    }
  }, [
    actions,
    state.playerName,
    state.playerProfile,
    asyncSearchService,
    playerDataService
  ]);

  const checkSearchStatus = useCallback(async (id?: number) => {
    const searchId = id || state.searchJob.id;
    if (!searchId) {
      console.log('No search ID to check status for');
      return;
    }
    
    try {
      await asyncSearchService.checkSearchStatus(searchId);
    } catch (error) {
      console.error('Error checking search status:', error);
      actions.setError(`Error: ${error instanceof Error ? error.message : 'Failed to check search status'}`);
    }
  }, [asyncSearchService, state.searchJob.id, actions]);

  const getSearchResults = useCallback(async (id?: number) => {
    const searchId = id || state.searchJob.id;
    if (!searchId) {
      console.log('No search ID to get results for');
      return;
    }
    
    try {
      const results = await asyncSearchService.getSearchResults(searchId);
      
      if (results) {
        // Cache the player if we have a profile
        if (state.playerProfile) {
          const cachedPlayer = {
            id: state.playerId,
            name: state.playerProfile.name || state.playerName,
            profile: state.playerProfile,
            data: results,
            injuries: state.playerInjuries || undefined,
            transfers: state.playerTransfers || undefined,
            achievements: state.playerAchievements || undefined,
            stats: state.playerStats || undefined
          };
          
          cache.addPlayer(cachedPlayer);
        }
      }
    } catch (error) {
      console.error('Error getting search results:', error);
      actions.setError(`Error: ${error instanceof Error ? error.message : 'Failed to get search results'}`);
    }
  }, [
    asyncSearchService,
    state.searchJob.id,
    state.playerProfile,
    state.playerId,
    state.playerName,
    state.playerInjuries,
    state.playerTransfers,
    state.playerAchievements,
    state.playerStats,
    actions,
    cache
  ]);

  const cancelSearch = useCallback(async () => {
    const searchId = state.searchJob.id;
    if (!searchId) {
      console.log('No search ID to cancel');
      return;
    }
    
    try {
      await asyncSearchService.cancelSearch(searchId);
    } catch (error) {
      console.error('Error cancelling search:', error);
      actions.setError(`Error: ${error instanceof Error ? error.message : 'Failed to cancel search'}`);
    }
  }, [asyncSearchService, state.searchJob.id, actions]);

  const clearSearch = useCallback(() => {
    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      console.log('Cancelling requests due to clear search');
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    }
    
    // Cancel async search
    if (state.searchJob.id) {
      asyncSearchService.cancelSearch(state.searchJob.id).catch(console.error);
    }
    
    // Reset all state
    actions.setPlayerName('');
    actions.setPlayerId('');
    actions.clearPlayerData();
    actions.setError(null);
    actions.setHasSearched(false);
    actions.resetSearchState();
  }, [actions, asyncSearchService, state.searchJob.id]);

  const checkAutomatedResults = useCallback(async (id: string, options?: { updateState?: boolean }) => {
    const { hasResults, data, timestamp } = await searchService.checkAutomatedResults(id);
    
    // Only update state if requested and results were found
    if (options?.updateState !== false && hasResults && data) {
      actions.setPlayerData(data);
      actions.setIsAutomatedResult(true);
      actions.setAutomatedResultTimestamp(timestamp || null);
      
      // Fetch supplementary data
      const supplementaryData = await playerDataService.fetchSupplementaryData(id);
      
      if (supplementaryData.injuries) actions.setPlayerInjuries(supplementaryData.injuries);
      if (supplementaryData.transfers) actions.setPlayerTransfers(supplementaryData.transfers);
      if (supplementaryData.achievements) actions.setPlayerAchievements(supplementaryData.achievements);
      
      // Make sure to set loading to false
      actions.setIsLoading(false);
    }
    
    return hasResults;
  }, [actions, searchService, playerDataService]);

  const initializeCategoryPolling = useCallback((searchId: string, pipelineType: 'parallel_categories' | 'parallel_categories_mock' = 'parallel_categories') => {
    const token = getToken();
    if (!token) {
      actions.setError("Authentication token not found.");
      return;
    }

    const pollingManager = new CategoryPollingManager(searchId, token, pipelineType, {
      onStatusUpdate: (statuses) => actions.setCategoryStatuses(statuses),
      onNewCategoryCompleted: (categories) => actions.addCompletedCategories(categories),
      onAllCategoriesCompleted: (allData) => {
        // Optionally handle all categories completed event
      },
      onError: (error) => actions.setError(error),
      onPollingComplete: () => actions.setIsPolling(false),
    });

    categoryPollingManagerRef.current = pollingManager;
    pollingManager.startPolling();
    actions.setIsPolling(true);
  }, [getToken, actions]);

  // Add cleanup effect to cancel any ongoing requests when the component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        console.log('Cancelling requests due to component unmount');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      if (categoryPollingManagerRef.current) {
        categoryPollingManagerRef.current.stopPolling();
        categoryPollingManagerRef.current = null;
      }

      searchService.abort();
      playerDataService.abort();
    };
  }, [searchService, playerDataService]);

  // Add effect to clear error messages when player data is available
  useEffect(() => {
    // If we have player data but also an error message about timeout, clear the error
    if (state.playerData && state.error && state.error.includes('Timed out while fetching results')) {
      console.log('Player data is available but timeout error is showing - clearing error');
      actions.setError(null);
    }
    
    // Set up a periodic check to clear any timeout errors if data is available
    const errorClearingInterval = setInterval(() => {
      if (state.playerData && state.error && state.error.includes('Timed out while fetching results')) {
        console.log('Periodic check: Player data is available but timeout error is showing - clearing error');
        actions.setError(null);
      }
    }, 1000); // Check every second
    
    return () => {
      clearInterval(errorClearingInterval);
    };
  }, [state.playerData, state.error, actions]);

  // Construct the final context value (the public API)
  const value: KYPSearchContextType = {
    // State
    ...state,
    
    // Actions
    ...actions,
    
    // Cache
    recentPlayers: cache.recentPlayers,
    loadCachedPlayer: cache.loadCachedPlayer,
    
    // Derived properties for backward compatibility
    searchId: state.searchJob.id,
    searchStatus: state.searchJob.status,
    searchProgress: state.searchJob.progress?.progress_percentage || 0,
    currentStep: state.searchJob.progress?.current_step || null,
    stepProgress: state.searchJob.progress?.step_progress || 0,
    completedSteps: state.searchJob.progress?.completed_steps || [],
    
    // Methods
    handleNameSearch,
    handlePlayerSearch,
    startAsyncSearch,
    checkSearchStatus,
    getSearchResults,
    cancelSearch,
    clearSearch,
    checkAutomatedResults,
    initializeCategoryPolling,
  };

  return (
    <KYPSearchContext.Provider value={value}>
      {children}
    </KYPSearchContext.Provider>
  );
};

/**
 * Hook to use the KYP search context
 * @returns The KYP search context
 * @throws If used outside of a KYPSearchProvider
 */
export const useKYPSearch = (): KYPSearchContextType => {
  const context = useContext(KYPSearchContext);
  if (context === undefined) {
    throw new Error('useKYPSearch must be used within a KYPSearchProvider');
  }
  return context;
};

// Re-export types for convenience
export type { 
  PlayerNameMatch,
  DetailedPlayerMatch,
  PlayerProfile,
  RatingData,
  PlayerData,
  CachedPlayer,
  PlayerInjury,
  PlayerTransfer,
  PlayerAchievement,
  PlayerStats,
  SearchJob,
  KYPSearchContextType,
  SearchStatus
} from './types/kyp-search.types';